import {
  ITEM_SIZE,
  LANE_COUNT,
  RELAY_HEIGHT,
  RUNNER_SIZE,
  RUNNER_Y,
  createInitialRelayState,
  createRelayItem,
  moveRelayLane,
  updateRelayGame,
} from '../neonRelayLogic';
import {
  ITEM_VISUALS,
  RELAY_WIDTH,
  getAvatarVisual,
  getRouteTheme,
} from '../neonRelayVisuals';

const LANE_WIDTH = RELAY_WIDTH / LANE_COUNT;
const LANE_CHANGE_THRESHOLD = 0.45;
const LANE_RESET_THRESHOLD = 0.25;
const STATE_EMIT_INTERVAL_MS = 120;
const RUNNER_DISPLAY_WIDTH = 58;
const RUNNER_DISPLAY_HEIGHT = 66;

function laneX(lane) {
  return LANE_WIDTH * lane + LANE_WIDTH / 2;
}

function createRect(scene, x, y, width, height, color, alpha = 1) {
  return scene.add.rectangle(x, y, width, height, color, alpha);
}

function stopObjectTweens(scene, gameObject) {
  if (!gameObject) {
    return;
  }

  scene.tweens?.killTweensOf?.(gameObject);

  if (Array.isArray(gameObject.list)) {
    gameObject.list.forEach((child) => stopObjectTweens(scene, child));
  }
}

function destroyRelayObject(scene, gameObject) {
  if (!gameObject) {
    return;
  }

  stopObjectTweens(scene, gameObject);
  gameObject.destroy?.();
}

export function cleanupNeonRelayScene(scene) {
  if (scene.cleanedUp) {
    return;
  }

  scene.cleanedUp = true;
  scene.time?.removeAllEvents?.();
  scene.cameras?.main?.stopShake?.();
  scene.tweens?.killAll?.();
  scene.itemSprites?.forEach((sprite) => {
    destroyRelayObject(scene, sprite);
  });
  scene.itemSprites?.clear?.();
  scene.speedLines = [];
}

function addWorldDecor(scene, ...children) {
  children.forEach((child) => {
    scene.worldDecor.add(child);
  });
}

function createItemSprite(scene, item) {
  const visual = ITEM_VISUALS[item.type] ?? ITEM_VISUALS.packet;
  const container = scene.add.container(laneX(item.lane), item.y);

  container.setData('itemType', item.type);
  container.setData('visualShape', visual.shape);
  container.setSize(ITEM_SIZE + 12, ITEM_SIZE + 12);

  if (item.type === 'boost') {
    const halo = scene.add.ellipse(0, 0, ITEM_SIZE + 14, ITEM_SIZE + 14, visual.primary, 0.16);
    const shell = createRect(scene, 0, 0, ITEM_SIZE - 2, ITEM_SIZE + 8, visual.primary, 0.96)
      .setStrokeStyle(2, visual.outline, 0.92);
    const capTop = createRect(scene, 0, -ITEM_SIZE / 2 - 6, ITEM_SIZE / 2, 5, visual.secondary, 1);
    const capBottom = createRect(scene, 0, ITEM_SIZE / 2 + 6, ITEM_SIZE / 2, 5, visual.secondary, 1);
    const core = scene.add.polygon(0, 1, [
      -3, -15,
      10, -3,
      2, -2,
      8, 15,
      -10, 1,
      -2, 0,
    ], visual.ink, 1);
    const charge = scene.add.line(0, 0, -10, 10, 10, -10, visual.secondary, 0.92)
      .setLineWidth(3);

    container.add([halo, shell, capTop, capBottom, core, charge]);
    scene.tweens.add({
      targets: halo,
      alpha: 0.34,
      scaleX: 1.14,
      scaleY: 1.14,
      duration: 460,
      yoyo: true,
      repeat: -1,
    });
    return container;
  }

  if (item.type === 'blocker') {
    const back = createRect(scene, 0, 0, ITEM_SIZE + 12, ITEM_SIZE + 2, visual.ink, 0.95)
      .setStrokeStyle(2, visual.outline, 0.86);
    const gate = createRect(scene, 0, 0, ITEM_SIZE + 2, ITEM_SIZE - 4, visual.primary, 0.96);
    const topFin = scene.add.triangle(0, -ITEM_SIZE / 2 - 8, -16, 8, 0, -5, 16, 8, visual.secondary, 0.95);
    const bottomFin = scene.add.triangle(0, ITEM_SIZE / 2 + 8, -16, -8, 0, 5, 16, -8, visual.secondary, 0.95);
    const slashOne = createRect(scene, 0, 0, ITEM_SIZE + 20, 5, visual.ink, 0.92);
    const slashTwo = createRect(scene, 0, 0, ITEM_SIZE + 20, 5, visual.ink, 0.92);
    const warningDot = scene.add.circle(0, 0, 4, visual.secondary, 1);

    slashOne.setRotation(Math.PI / 4);
    slashTwo.setRotation(-Math.PI / 4);
    container.add([back, gate, topFin, bottomFin, slashOne, slashTwo, warningDot]);
    return container;
  }

  const parcel = createRect(scene, 0, 0, ITEM_SIZE + 4, ITEM_SIZE, visual.primary, 0.97)
    .setStrokeStyle(2, visual.outline, 0.92);
  const flap = scene.add.triangle(0, -2, -ITEM_SIZE / 2, -ITEM_SIZE / 2 + 3, 0, 3, ITEM_SIZE / 2, -ITEM_SIZE / 2 + 3, visual.ink, 0.3);
  const address = createRect(scene, 2, -2, ITEM_SIZE - 14, 8, visual.secondary, 1);
  const chipA = createRect(scene, -ITEM_SIZE / 2 + 7, ITEM_SIZE / 2 - 7, 5, 5, visual.ink, 0.88);
  const chipB = createRect(scene, ITEM_SIZE / 2 - 7, ITEM_SIZE / 2 - 7, 5, 5, visual.ink, 0.88);
  const contact = scene.add.line(0, 0, -8, 9, 8, 9, visual.outline, 0.8)
    .setLineWidth(2);

  container.add([parcel, flap, address, chipA, chipB, contact]);
  return container;
}

function createFallbackRunner(scene, avatar) {
  const fallback = scene.add.container(0, 0);
  const body = scene.add.triangle(0, -3, 0, -RUNNER_SIZE / 2, RUNNER_SIZE / 2, RUNNER_SIZE / 3, -RUNNER_SIZE / 2, RUNNER_SIZE / 3, avatar.trail, 1);
  const core = scene.add.circle(0, -10, 8, avatar.accent, 1);
  const pack = createRect(scene, 0, 13, 20, 20, 0x101a2b, 0.92);

  body.setStrokeStyle(2, 0xf7fbff, 0.85);
  pack.setStrokeStyle(1, avatar.accent, 0.8);
  fallback.add([body, pack, core]);
  return fallback;
}

function createRunner(scene, selectedPlayableIcon) {
  const avatar = getAvatarVisual(selectedPlayableIcon);
  const container = scene.add.container(laneX(1), RUNNER_Y);
  const shadow = scene.add.ellipse(0, RUNNER_SIZE / 2 + 5, RUNNER_SIZE + 24, 15, 0x000000, 0.34);
  const rim = scene.add.ellipse(0, -1, RUNNER_DISPLAY_WIDTH + 12, RUNNER_DISPLAY_HEIGHT + 8, avatar.trail, 0.15)
    .setStrokeStyle(2, avatar.accent, 0.5);
  const avatarNode = scene.textures.exists(avatar.textureKey)
    ? scene.add.image(0, -4, avatar.textureKey).setDisplaySize(RUNNER_DISPLAY_WIDTH, RUNNER_DISPLAY_HEIGHT)
    : createFallbackRunner(scene, avatar);

  avatarNode.setData?.('avatarId', selectedPlayableIcon);
  container.avatarNode = avatarNode;
  container.rim = rim;
  container.add([shadow, rim, avatarNode]);

  scene.tweens.add({
    targets: rim,
    alpha: 0.3,
    scaleX: 1.06,
    scaleY: 1.04,
    duration: 720,
    yoyo: true,
    repeat: -1,
  });

  return container;
}

function drawRouteWorld(scene) {
  const theme = scene.theme;

  scene.add.rectangle(RELAY_WIDTH / 2, RELAY_HEIGHT / 2, RELAY_WIDTH, RELAY_HEIGHT, theme.background, 1);

  for (let index = 0; index < 8; index += 1) {
    const width = 24 + (index % 3) * 12;
    const height = 42 + (index % 4) * 24;
    const x = 18 + index * 48;
    const y = 95 - (index % 2) * 12;
    const tower = scene.add.rectangle(x, y, width, height, theme.horizon, 0.55);
    const sign = scene.add.text(x - width / 2 + 4, y - height / 2 + 6, theme.sign, {
      fontFamily: 'monospace',
      fontSize: '8px',
      color: '#f7fbff',
    }).setAlpha(0.34);

    addWorldDecor(scene, tower, sign);
  }

  for (let lane = 0; lane < LANE_COUNT; lane += 1) {
    const x = laneX(lane);
    const fill = lane % 2 === 0 ? theme.laneA : theme.laneB;
    const lanePanel = scene.add.rectangle(x, RELAY_HEIGHT / 2, LANE_WIDTH, RELAY_HEIGHT, fill, 0.7);
    const laneCenter = scene.add.line(0, 0, x, 0, x, RELAY_HEIGHT, theme.secondary, 0.08)
      .setOrigin(0, 0)
      .setLineWidth(2);
    const divider = scene.add.line(0, 0, lane * LANE_WIDTH, 0, lane * LANE_WIDTH, RELAY_HEIGHT, theme.secondary, 0.2)
      .setOrigin(0, 0);

    addWorldDecor(scene, lanePanel, laneCenter, divider);
  }

  addWorldDecor(
    scene,
    scene.add.line(0, 0, RELAY_WIDTH - 1, 0, RELAY_WIDTH - 1, RELAY_HEIGHT, theme.secondary, 0.2)
      .setOrigin(0, 0)
  );

  for (let index = 0; index < 14; index += 1) {
    const line = scene.add.rectangle(
      20 + ((index * 29) % (RELAY_WIDTH - 40)),
      (index * 67) % RELAY_HEIGHT,
      2,
      30 + (index % 3) * 18,
      index % 2 === 0 ? theme.accent : theme.secondary,
      0.18
    );

    scene.speedLines.push(line);
  }

  scene.add.rectangle(RELAY_WIDTH / 2, RUNNER_Y, RELAY_WIDTH - 26, 2, theme.accent, 0.3);
}

function createPickupBurst(scene, x, y, color, label) {
  const burst = scene.add.container(x, y);
  const ring = scene.add.ellipse(0, 0, 18, 18, color, 0.1)
    .setStrokeStyle(2, color, 0.9);
  const sparkA = createRect(scene, 0, -16, 4, 12, color, 0.9);
  const sparkB = createRect(scene, 14, 6, 4, 10, color, 0.8).setRotation(Math.PI / 3);
  const sparkC = createRect(scene, -14, 6, 4, 10, color, 0.8).setRotation(-Math.PI / 3);
  const text = scene.add.text(0, -36, label, {
    fontFamily: 'monospace',
    fontSize: '13px',
    color: '#f7fbff',
    stroke: '#101a2b',
    strokeThickness: 3,
  }).setOrigin(0.5);

  burst.add([ring, sparkA, sparkB, sparkC, text]);
  scene.tweens.add({
    targets: ring,
    scaleX: 2.2,
    scaleY: 2.2,
    alpha: 0,
    duration: scene.reducedMotion ? 140 : 280,
  });
  scene.tweens.add({
    targets: [sparkA, sparkB, sparkC, text],
    y: '-=18',
    alpha: 0,
    duration: scene.reducedMotion ? 160 : 360,
    onComplete: () => burst.destroy(),
  });
}

function createLaneSwitchTrail(scene, direction) {
  if (scene.reducedMotion) {
    return;
  }

  const avatar = scene.avatarVisual;
  const ghost = scene.add.ellipse(scene.runner.x - direction * 8, RUNNER_Y, 28, 46, avatar.trail, 0.1)
    .setStrokeStyle(1, avatar.accent, 0.26);

  scene.tweens.add({
    targets: ghost,
    alpha: 0,
    scaleX: 1.16,
    duration: 140,
    onComplete: () => ghost.destroy(),
  });
}

function createStreakBreak(scene) {
  if (scene.reducedMotion) {
    return;
  }

  const text = scene.add.text(RELAY_WIDTH / 2, RUNNER_Y - 72, 'CHAIN BROKEN', {
    fontFamily: 'monospace',
    fontSize: '12px',
    color: '#ffd166',
    stroke: '#101a2b',
    strokeThickness: 3,
  }).setOrigin(0.5).setAlpha(0.86);

  scene.tweens.add({
    targets: text,
    y: text.y - 20,
    alpha: 0,
    duration: 520,
    onComplete: () => text.destroy(),
  });
}

function createRouteFinishEffect(scene, isFailure) {
  const color = isFailure ? ITEM_VISUALS.blocker.primary : scene.theme.accent;
  const label = isFailure ? 'SIGNAL LOST' : 'ROUTE CLEAR';
  const beam = createRect(scene, RELAY_WIDTH / 2, RELAY_HEIGHT / 2, RELAY_WIDTH - 54, 0, color, 0.18);
  const text = scene.add.text(RELAY_WIDTH / 2, RELAY_HEIGHT / 2 - 24, label, {
    fontFamily: 'monospace',
    fontSize: '22px',
    color: '#f7fbff',
    stroke: '#101a2b',
    strokeThickness: 5,
  }).setOrigin(0.5).setAlpha(0);

  scene.tweens.add({
    targets: beam,
    height: isFailure ? 120 : 210,
    alpha: isFailure ? 0.12 : 0.24,
    duration: scene.reducedMotion ? 120 : 260,
    yoyo: !scene.reducedMotion,
  });
  scene.tweens.add({
    targets: text,
    alpha: 1,
    y: text.y - 10,
    duration: scene.reducedMotion ? 120 : 260,
    onComplete: () => {
      scene.tweens.add({
        targets: [beam, text],
        alpha: 0,
        delay: scene.reducedMotion ? 120 : 360,
        duration: 240,
        onComplete: () => {
          beam.destroy();
          text.destroy();
        },
      });
    },
  });
}

function syncItems(scene) {
  const activeIds = new Set(scene.relay.items.map((item) => item.id));

  scene.itemSprites.forEach((sprite, id) => {
    if (!activeIds.has(id)) {
      destroyRelayObject(scene, sprite);
      scene.itemSprites.delete(id);
    }
  });

  scene.relay.items.forEach((item) => {
    let sprite = scene.itemSprites.get(item.id);

    if (!sprite) {
      sprite = createItemSprite(scene, item);
      scene.itemSprites.set(item.id, sprite);
    }

    sprite.setPosition(laneX(item.lane), item.y);
  });
}

function updateWorldMotion(scene, deltaMs) {
  const speed = (scene.route.speed * deltaMs) / 1000;

  scene.speedLines.forEach((line, index) => {
    line.y += speed * (0.7 + (index % 3) * 0.24);

    if (line.y > RELAY_HEIGHT + 40) {
      line.y = -40;
    }
  });
}

function emitState(scene, force = false) {
  if (!force && scene.time.now - scene.lastStateEmitAt < STATE_EMIT_INTERVAL_MS) {
    return;
  }

  scene.lastStateEmitAt = scene.time.now;
  scene.eventsConfig.onStateChange?.(scene.relay);
}

function emitFeedback(scene, previous, next) {
  if (next.score > previous.score) {
    scene.eventsConfig.onScore?.(next.score);
  }

  if (next.integrity < previous.integrity) {
    scene.eventsConfig.onDamage?.(next.integrity);
    createPickupBurst(scene, laneX(previous.lane), RUNNER_Y - 8, ITEM_VISUALS.blocker.primary, '- CORE');
    if (!scene.reducedMotion) {
      scene.cameras.main.shake(110, 0.004);
      scene.runner.avatarNode?.setTint?.(ITEM_VISUALS.blocker.primary);
      scene.time.delayedCall(160, () => scene.runner.avatarNode?.clearTint?.());
    }
  }

  if (next.packets > previous.packets) {
    scene.eventsConfig.onPacket?.(next.packets);
    createPickupBurst(scene, laneX(previous.lane), RUNNER_Y - 16, ITEM_VISUALS.packet.secondary, `+${next.streak}`);
  }

  if (next.score > previous.score && next.packets === previous.packets && next.integrity === previous.integrity) {
    createPickupBurst(scene, laneX(previous.lane), RUNNER_Y - 16, ITEM_VISUALS.boost.primary, '+TIME');
    scene.runner.rim?.setStrokeStyle(3, ITEM_VISUALS.boost.secondary, 0.8);
    scene.time.delayedCall(260, () => scene.runner.rim?.setStrokeStyle(2, scene.avatarVisual.accent, 0.5));
  }

  if (next.streak < previous.streak && next.integrity === previous.integrity && next.packets === previous.packets) {
    createStreakBreak(scene);
  }
}

function finishRoute(scene, next) {
  emitState(scene, true);
  createRouteFinishEffect(scene, next.status === 'gameOver');

  if (next.status === 'gameOver') {
    scene.eventsConfig.onGameOver?.(next);
    return;
  }

  if (next.status === 'routeComplete') {
    scene.eventsConfig.onRouteComplete?.(next);
  }
}

function readLaneIntent(scene) {
  const input = scene.controllerInputRef?.current ?? {};
  const nextDirection = input.moveX < -LANE_CHANGE_THRESHOLD
    ? -1
    : input.moveX > LANE_CHANGE_THRESHOLD
      ? 1
      : 0;

  if (nextDirection === 0) {
    if (Math.abs(input.moveX ?? 0) < LANE_RESET_THRESHOLD) {
      scene.laneInputDirection = 0;
    }
    return;
  }

  if (nextDirection !== scene.laneInputDirection) {
    scene.laneInputDirection = nextDirection;
    scene.relay = moveRelayLane(scene.relay, nextDirection);
    scene.runnerTargetX = laneX(scene.relay.lane);
    scene.eventsConfig.onLaneChange?.(scene.relay.lane);
    createLaneSwitchTrail(scene, nextDirection);

    if (!scene.reducedMotion) {
      scene.tweens.killTweensOf(scene.runner.avatarNode);
      scene.runner.avatarNode.setAngle?.(nextDirection * 3);
      scene.tweens.add({
        targets: scene.runner.avatarNode,
        angle: 0,
        duration: 105,
      });
    }

    emitState(scene, true);
  }
}

export function createNeonRelayScene({
  controllerInputRef,
  definition,
  events = {},
  reducedMotion = false,
  routeIndex = 0,
  score = 0,
  selectedPlayableIcon = 'neon-relay',
}) {
  return {
    key: 'NeonRelayScene',

    init() {
      this.controllerInputRef = controllerInputRef;
      this.definition = definition;
      this.eventsConfig = events;
      this.reducedMotion = reducedMotion;
      this.routeIndex = routeIndex;
      this.route = definition.routes[routeIndex] ?? definition.routes[0];
      this.theme = getRouteTheme(this.route);
      this.avatarVisual = getAvatarVisual(selectedPlayableIcon);
      this.relay = {
        ...createInitialRelayState(routeIndex, definition),
        status: 'playing',
        score,
      };
      this.spawnTimerMs = 260;
      this.laneInputDirection = 0;
      this.lastStateEmitAt = 0;
      this.runnerTargetX = laneX(this.relay.lane);
      this.itemSprites = new Map();
      this.speedLines = [];
      this.cleanedUp = false;
    },

    preload() {
      const publicUrl = process.env.PUBLIC_URL || '';
      this.load.image(this.avatarVisual.textureKey, `${publicUrl}${this.avatarVisual.assetPath}`);
    },

    create() {
      const cleanup = () => cleanupNeonRelayScene(this);

      this.events.once('shutdown', cleanup);
      this.events.once('destroy', cleanup);
      this.worldDecor = this.add.group();
      drawRouteWorld(this);
      this.activeLane = this.add.rectangle(laneX(this.relay.lane), RELAY_HEIGHT / 2, LANE_WIDTH - 8, RELAY_HEIGHT - 16, this.theme.accent, 0.1)
        .setStrokeStyle(2, this.theme.accent, 0.34);
      this.runner = createRunner(this, selectedPlayableIcon);
      this.eventsConfig.onReady?.(this.relay);
      emitState(this, true);
    },

    update(_, deltaMs) {
      if (this.relay.status !== 'playing') {
        return;
      }

      updateWorldMotion(this, deltaMs);
      readLaneIntent(this);

      this.spawnTimerMs -= deltaMs;
      const spawnedItems = [];

      if (this.spawnTimerMs <= 0) {
        spawnedItems.push(createRelayItem(this.relay.nextItemId, this.route, () => Math.random()));
        this.spawnTimerMs = this.route.spawnRateMs;
      }

      const previous = this.relay;
      const next = updateRelayGame(previous, {
        deltaMs,
        route: this.route,
        spawnedItems,
      });

      this.relay = next;
      this.activeLane.x = laneX(next.lane);
      this.runner.x += (this.runnerTargetX - this.runner.x) * 0.34;
      syncItems(this);
      emitFeedback(this, previous, next);
      emitState(this);

      if (next.status !== 'playing') {
        finishRoute(this, next);
      }
    },
  };
}
